import { MilestoneRewardType } from "constants/League";
import * as Yup from "yup";

// file type validation
const imageFileTypeValidation = Yup.string().test(
  "is-png",
  "File must be a PNG",
  (value) => !!value?.match(/^image\/(png|jpeg|heic|webp|jpg|heic|svg)$/)
);

// Pack Validation

const boosterProbabilitySchema = Yup.object().shape({
  boosterId: Yup.mixed().required(),
  probability: Yup.number()
    .positive()
    .required("Booster Probability is required")
    .min(0.00001, "Booster Probability must be at least 1")
    .max(100, "Booster Probability must not exceed 100"),
});

const dropProbabilitySchema = Yup.object().shape({
  ovrMin: Yup.number().positive().min(1).max(100).required(),
  ovrMax: Yup.number().positive().min(1).max(100).required(),
  probability: Yup.number()
    .positive()
    .required("Drop Probability is required")
    .min(0.0001, "Drop Probability must be at least 1")
    .max(100, "Drop Probability must not exceed 100"),
});

const slotSchema = Yup.object().shape({
  type: Yup.string(),
  amount: Yup.number().when("type", {
    is: "Currency",
    then: Yup.number().min(1, "Amount must be at least 1"),
    otherwise: Yup.number().nullable().notRequired(),
  }),
  playerPool: Yup.array().when("type", {
    is: "PlayerPool",
    then: Yup.array()
      .of(Yup.mixed().required("Player is required for type 'PlayerPool'"))
      .min(1, "At least one player is required in 'PlayerPool'"),
    otherwise: Yup.array().optional(),
  }),
  playerCard: Yup.mixed().when("type", {
    is: "NamedPlayer",
    then: Yup.mixed().required('Player is required for type "NamedPlayer"'),
    otherwise: Yup.mixed().optional(),
  }),
  dropProbabilities: Yup.array().when("type", {
    is: "Player",
    then: Yup.array()
      .of(dropProbabilitySchema)
      .min(1, "At least one drop probability is required")
      .required('Drop probabilities are required for type "Player"')
      .test(
        "total-drop-probability",
        "Drop probability sum should be 100",
        function (value) {
          if (!value || value.length === 0) return true;
          const totalProbability = value.reduce(
            (sum, item) => sum + item.probability,
            0
          );
          return Math.round(totalProbability) === 100;
        }
      ),
    otherwise: Yup.array().optional(),
  }),
  boosterProbabilities: Yup.array().when("type", {
    is: "Booster",
    then: Yup.array()
      .of(boosterProbabilitySchema)
      .min(1, "At least one booster probability is required")
      .required('Booster probabilities are required for type "Booster"')
      .test(
        "total-booster-probability",
        "Booster probability sum should be 100",
        function (value) {
          if (!value || value.length === 0) return true;
          const totalProbability = value.reduce(
            (sum, item) => sum + item.probability,
            0
          );
          return totalProbability === 100;
        }
      ),
    otherwise: Yup.array().optional(),
  }),
});

export const packSchema = Yup.object().shape({
  name: Yup.string().required(),
  type: Yup.string().required(),
  summary: Yup.string().optional().nullable(),
  isDisabled: Yup.boolean().required(),
  price: Yup.number().when("type", {
    is: "Store",
    then: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value
      )
      .required(),
    otherwise: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ||
        originalValue === null ||
        (typeof originalValue === "number" && isNaN(originalValue))
          ? undefined
          : value
      )
      .optional()
      .nullable(),
  }),
  requiredVaultLevel: Yup.number()
    .nullable()
    .optional()
    .positive("Required Vault Level must be a positive number.")
    .integer("Required Vault Level must be a whole number."),
  imageUrl: Yup.string().optional().nullable(),
  slots: Yup.array()
    .of(
      slotSchema.test({
        name: "custom-slot-error",
        test: (value, context) => {
          if (!value || !value.type) {
            const index = Number(context.path.split("[")[1].split("]")[0]);
            return context.createError({
              message: `Slot ${index + 1}'s type is required`,
            });
          }
          return true;
        },
      })
    )
    .required(),
  isFTUPack: Yup.boolean().required(),
  packPredefinedImageType: Yup.string().optional(),
  // fileType only matters for a freshly uploaded image. A predefined image, a library image
  // (packImageId) or an image the pack already has are each a complete source on their own.
  fileType: Yup.string()
    .when(["packPredefinedImageType", "imageUrl", "packImageId"], {
      is: (packPredefinedImageType, imageUrl, packImageId) =>
        !packPredefinedImageType && !imageUrl && !packImageId,
      then: Yup.string().required(),
      otherwise: Yup.string().optional(),
    })
    .optional(),
});

// League Validation
const numberOrUndefined = (value, originalValue) =>
  originalValue === "" ||
  originalValue === null ||
  (typeof originalValue === "number" && isNaN(originalValue))
    ? undefined
    : value;

const milestoneSchema = Yup.object()
  .shape({
    number: Yup.number().positive().required("Milestone 'Number' is required."),
    steps: Yup.number().positive().required("Milestone 'steps' is required."),
    rewardType: Yup.mixed().oneOf(Object.values(MilestoneRewardType)).optional(),
    rewardPack: Yup.mixed().when("rewardType", {
      is: (rewardType) =>
        rewardType === MilestoneRewardType.Pack ||
        rewardType === MilestoneRewardType.Both,
      then: Yup.mixed().required(
        "'Reward pack' is required for the selected reward type."
      ),
      otherwise: Yup.mixed().nullable(),
    }),
    rewardCoins: Yup.number().when("rewardType", {
      is: (rewardType) =>
        rewardType === MilestoneRewardType.Coin ||
        rewardType === MilestoneRewardType.Both,
      then: Yup.number()
        .transform(numberOrUndefined)
        .positive("'Milestone rewardCoin' must be greater than 0.")
        .required(
          "'Milestone rewardCoin' is required for the selected reward type."
        ),
      otherwise: Yup.number()
        .transform(numberOrUndefined)
        .optional()
        .nullable()
        .min(0, "'Milestone rewardCoin' must be 0 or greater."),
    }),
    // A "Coins only" milestone can still hold a pack the admin picked earlier - the save payload
    // drops it - so the count is only required when the chosen type actually uses the pack.
    rewardPackCount: Yup.number().when(["rewardType", "rewardPack"], {
      is: (rewardType, rewardPack) =>
        rewardType !== MilestoneRewardType.Coin && Boolean(rewardPack),
      then: Yup.number()
        .transform(numberOrUndefined)
        .positive("'Pack count' must be greater than 0.")
        .required("'Pack count' is required when a Reward Pack is selected."),
      otherwise: Yup.number().transform(numberOrUndefined).optional().nullable(),
    }),
  })
  .test(
    "reward-pack-or-coins",
    "Each milestone must have a 'Reward pack' or 'Milestone rewardCoin' configured.",
    (milestone) => Boolean(milestone?.rewardPack) || Boolean(milestone?.rewardCoins)
  );

export const leagueSchema = Yup.object().shape({
  name: Yup.string().required(),
  number: Yup.number().positive().required(),
  k1: Yup.number().positive().required(),
  k2: Yup.number().positive().required(),
  milestones: Yup.array().of(milestoneSchema).min(1),
});

export const storeValidations = Yup.object().shape({
  pbfCoin: Yup.number()
    .typeError("'PBF Coin' must be a number")
    .min(0, "'PBF Coin' must be 0 or a positive number"),
  buyPrice: Yup.lazy((value) =>
    Yup.object(
      Object.keys(value || {}).reduce((acc, key) => {
        acc[key] = Yup.number()
          .typeError(`${key} must be a number`)
          .required(`${key} is required`)
          .positive(`${key} must be a positive number`);
        return acc;
      }, {})
    )
  ),
  bonus: Yup.number()
    .typeError("'Bonus' must be a number")
    .min(0, "'Bonus' must be 0 or positive number"),
  // buyCoins: Yup.number()
  //   .typeError("'Buy Coins' must be a number")
  //   .min(0, "'Buy Coins' must be 0 or positive number"),
  // bonusTxt: Yup.string().required("'Image Url' is required"),
});

const commonFileTypeChecker = (v, isNullable = false) => {
  let _val = Yup.string().when(v, {
    is: (value) => !value,
    then: imageFileTypeValidation,
    otherwise: Yup.string().optional(),
  });
  return isNullable ? Yup.string().optional() : _val;
};

export const cardBGValidation = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  textColor: Yup.string().required("Text Color is required"),
  cardImgFileType: commonFileTypeChecker("cardImg", true),
  cardImgSmFileType: commonFileTypeChecker("cardImgSm", true),
  detImgFileType: commonFileTypeChecker("detImg", true),
});

/// brand validation
export const brandValidation = Yup.object().shape({
  platform: Yup.string().required(),
  operator: Yup.string().required(),
  brandName: Yup.string().required(),
  winTax: Yup.number().optional().min(0),
  betTax: Yup.number().optional().min(0),
  domain: Yup.string().optional(),
});

export const marketMarginValidation = Yup.object().test(
  "is-positive",
  "All market values must be positive numbers",
  (value) => {
    return (
      value &&
      Object.values(value).every(
        (num) =>
          ![null, undefined].includes(num) &&
          typeof num === "number" &&
          num >= 0
      )
    );
  }
);

export const operatorValidation = Yup.object().shape({
  operatorName: Yup.string().required(),
  platform: Yup.string().required(),
  betLimits: Yup.array(
    Yup.object().shape({
      currency: Yup.string().required("Currency is required"),
      minBet: Yup.number()
        .moreThan(0, "Minimum bet must be greater than 0")
        .required("Minimum bet is required"),
      maxBet: Yup.number()
        .min(
          Yup.ref("minBet"),
          "Max bet must be greater than or equal to min bet"
        )
        .required("Maximum bet is required"),
      maxPayOut: Yup.number()
        .min(1, "Max payout must be at least 1")
        .required("Max payout is required"),
    })
  ),
});

export const notificationValidation = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  body: Yup.string().required("Body is required"),
  isActive: Yup.boolean().required("Status is required"),
  territory: Yup.string().required("Territory is required"),
  fileType: commonFileTypeChecker("imageUrl", true),
});

export const storeBannerValidation = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  body: Yup.string().required("Body is required"),
  isActive: Yup.boolean().required("Status is required"),
  fileType: commonFileTypeChecker("imageUrl"),
  order: Yup.number().positive().min(1).required("order is required"),
  textColor: Yup.string().required("Text Color is required"),
});

export const eosRewardConfigValidation = Yup.object().shape({
  tier: Yup.number().required("Tier is required"),
  startPoints: Yup.number().required("Start points are required"),
  endPoints: Yup.number()
    .required("End points are required")
    .moreThan(
      Yup.ref("startPoints"),
      "End points must be greater than start points"
    ),
  rewardCoins: Yup.number()
    .typeError("PBF coins must be a number")
    .integer("PBF coins must be a whole number")
    .min(0, "PBF coins cannot be negative")
    .required("PBF coins are required"),
  rewards: Yup.array()
    .of(
      Yup.object().shape({
        rewardPack: Yup.mixed()
          .nullable()
          .required("'Reward pack' is required.")
          .test(
            "not-empty",
            "'Reward pack' cannot be empty",
            (value) => value !== ""
          ),
        rewardPackCount: Yup.number().positive().required("Count is required"),
      })
    )
    .min(1, "At least one reward is required"),
});

// Vault Reward Config Validation
const vaultRewardSchema = Yup.object().shape({
  probability: Yup.number()
    .positive("Probability must be greater than 0")
    .max(100, "Probability cannot exceed 100")
    .required("Probability is required"),
  slotType: Yup.string()
    .oneOf(["Player", "Coins", "Booster"], "Invalid slot type")
    .required("Slot type is required"),
  playerOvr: Yup.number().when("slotType", {
    is: "Player",
    then: Yup.number()
      .positive("Player OVR must be greater than 0")
      .max(100, "Player OVR cannot exceed 100")
      .required("Player OVR is required for Player rewards"),
    otherwise: Yup.number().nullable(),
  }),
  coins: Yup.number().when("slotType", {
    is: "Coins",
    then: Yup.number()
      .positive("Coins must be greater than 0")
      .required("Coins amount is required for Coin rewards"),
    otherwise: Yup.number().nullable(),
  }),
  booster: Yup.mixed().when("slotType", {
    is: "Booster",
    then: Yup.mixed().required("Booster selection is required for Booster rewards"),
    otherwise: Yup.mixed().nullable(),
  }),
});

export const vaultRewardConfigValidation = Yup.object().shape({
  level: Yup.number()
    .min(0, "Level must be greater than or equal to 0")
    .required("Level is required"),
  rewards: Yup.array()
    .of(vaultRewardSchema)
    .min(1, "At least one reward is required")
    .test(
      "total-probability",
      "Total probability must equal 100%",
      function (value) {
        if (!value || value.length === 0) return true;
        const totalProbability = value.reduce(
          (sum, reward) => sum + (Number(reward.probability) || 0),
          0
        );
        return totalProbability === 100;
      }
    ),
});

export const dailyRewardCycleSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  isDefault: Yup.boolean().required(),
  startDate: Yup.date()
    .nullable()
    .when("isDefault", {
      is: false,
      then: (schema) => schema.required("Week is required for a weekly cycle"),
    }),
  slots: Yup.array()
    .of(
      Yup.object().shape({
        slotNo: Yup.number().min(1).max(7).required(),
        awardType: Yup.string()
          .oneOf(["COINS", "PACK", "BOOSTER", "XP"])
          .required("Reward type is required"),
        rewardHC: Yup.number()
          .nullable()
          .when("awardType", {
            is: "COINS",
            then: (schema) =>
              schema.required("Coins amount is required").positive(),
          }),
        xpAmount: Yup.number()
          .nullable()
          .when("awardType", {
            is: "XP",
            then: (schema) =>
              schema.required("XP amount is required").positive(),
          }),
        pack: Yup.string()
          .nullable()
          .when("awardType", {
            is: "PACK",
            then: (schema) => schema.required("Pack is required"),
          }),
        booster: Yup.string()
          .nullable()
          .when("awardType", {
            is: "BOOSTER",
            then: (schema) => schema.required("Booster is required"),
          }),
      })
    )
    .length(7, "All 7 days must be configured")
    .required(),
});
